import React from 'react'
import styled from '@emotion/styled'
import { Divider, Empty, Modal, QRCode } from 'antd'
import { valueType } from 'antd/es/statistic/utils'

import { IMG_STORAGE_URL } from '@src/utils/constants'
import { getDateByTimezone } from '@src/utils/utils'
import { CompanyChallengeTableItem } from '../type'

type Props = {
	isChallengePreviewModalOpened: boolean
	onChallengePreviewModalCanceled: () => void
	selectedTableItem: CompanyChallengeTableItem | undefined
	priceFormatter: (price: valueType | undefined) => string
}

export const HotdealChallengePreviewModal: React.FC<Props> = ({
	isChallengePreviewModalOpened,
	onChallengePreviewModalCanceled,
	selectedTableItem,
	priceFormatter,
}: Props) => {
	return (
		<Modal
			open={isChallengePreviewModalOpened}
			onCancel={onChallengePreviewModalCanceled}
			title={<div style={{ height: '24px' }} />}
			footer={null}
			width={375}
			centered
		>
			<PreviewModalBodyContainer>
				{selectedTableItem ? (
					<>
						{selectedTableItem.challengeId ? (
							<ChallengeLinkArea>
								<QRCode
									value={`https://${
										process.env.NODE_ENV !== 'production' ? 'qa.' : ''
									}motiners.com/campaign/${selectedTableItem.challengeId}`}
									size={72}
									bordered={false}
								/>
								<ChallengeLinkDescription>모바일로 보기</ChallengeLinkDescription>
							</ChallengeLinkArea>
						) : null}

						{selectedTableItem.challengeCoverImage ? (
							<ChallengeCoverArea>
								<ChallengeCoverImage
									src={`${IMG_STORAGE_URL}/campaign_cover_img/${selectedTableItem.challengeCoverImage}`}
								/>
							</ChallengeCoverArea>
						) : null}

						{selectedTableItem.challengeName ? (
							<ChallengeNameArea>
								<ChallengeName>{selectedTableItem.challengeName}</ChallengeName>
							</ChallengeNameArea>
						) : null}

						{selectedTableItem.certificationStartDate ||
						selectedTableItem.certificationEndDate ||
						selectedTableItem.taskName ? (
							<CertificationArea>
								<CertificationKey>루틴 인증</CertificationKey>
								<CertificationContent>
									<CertificationDuration>
										<CertificationDurationKey>인증 기간</CertificationDurationKey>
										<CertificationDurationValue>{`${
											selectedTableItem.certificationStartDate
												? getDateByTimezone(
														'Asia/Seoul',
														'MM/dd',
														selectedTableItem.certificationStartDate,
												  )
												: ''
										}~${
											selectedTableItem.certificationEndDate
												? getDateByTimezone(
														'Asia/Seoul',
														'MM/dd',
														selectedTableItem.certificationEndDate,
												  )
												: ''
										}`}</CertificationDurationValue>
									</CertificationDuration>
									<CertificationTask>
										<CertificationTaskKey>월~일</CertificationTaskKey>
										<CertificationTaskValue>
											{selectedTableItem.taskName || '-'}
										</CertificationTaskValue>
									</CertificationTask>
								</CertificationContent>
							</CertificationArea>
						) : null}

						{selectedTableItem.certificationExampleImages &&
						selectedTableItem.certificationExampleImages.length > 0 ? (
							<>
								<Divider
									style={{
										background: '#efefef',
										margin: '0 20px',
										width: 'auto',
										minWidth: 'auto',
									}}
								/>
								<CertificationMethodArea>
									<CertificationMethodTitle>어떻게 인증하나요?</CertificationMethodTitle>
									<CertificationMethodImageWrapper>
										{selectedTableItem.certificationExampleImages.map(
											(certificationExampleImage) => (
												<CertificationMethodImage
													key={certificationExampleImage}
													src={`${IMG_STORAGE_URL}/campaign_sample_proof_img/${certificationExampleImage}`}
												/>
											),
										)}
									</CertificationMethodImageWrapper>
								</CertificationMethodArea>
							</>
						) : null}

						{selectedTableItem.hotdealItemImage ||
						selectedTableItem.hotdealItemName ||
						selectedTableItem.beforePrice ||
						selectedTableItem.afterPrice ? (
							<>
								<Divider
									style={{
										background: '#efefef',
										margin: '0 20px',
										width: 'auto',
										minWidth: 'auto',
									}}
								/>
								<ChallengeRewardArea>
									<ChallengeRewardTitle>성공하면 이 가격에!🔥</ChallengeRewardTitle>
									<ChallengeRewardSubTitle>기간 내 3회 인증 시, 받는 혜택!</ChallengeRewardSubTitle>
									<HotdealItemWrapper>
										<HotdealItemLeft>
											<HotdealItemImageWrapper>
												{selectedTableItem.hotdealItemImage ? (
													<HotdealItemImage
														src={`${IMG_STORAGE_URL}/hotdeal/hotdeal_product_img/${selectedTableItem.hotdealItemImage}`}
													/>
												) : null}
											</HotdealItemImageWrapper>
										</HotdealItemLeft>
										<HotdealItemRight>
											<HotdealItemName>{selectedTableItem.hotdealItemName || '-'}</HotdealItemName>
											<HotdealItemPriceInfo>
												<HotdealItemBeforePrice>
													{priceFormatter(selectedTableItem.beforePrice || undefined) || '-'}
												</HotdealItemBeforePrice>
												<HotdealItemPriceBottom>
													<HotdealItemDiscount>
														{`${
															selectedTableItem.beforePrice && selectedTableItem.afterPrice
																? Math.round(
																		(1 -
																			selectedTableItem.afterPrice /
																				selectedTableItem.beforePrice) *
																			100,
																  )
																: '-'
														}%`}
													</HotdealItemDiscount>
													<HotdealItemAfterPrice>
														{`${
															priceFormatter(selectedTableItem.afterPrice || undefined) || '-'
														}원`}
													</HotdealItemAfterPrice>
												</HotdealItemPriceBottom>
											</HotdealItemPriceInfo>
										</HotdealItemRight>
									</HotdealItemWrapper>
								</ChallengeRewardArea>
							</>
						) : null}

						{selectedTableItem.descriptionImages &&
						selectedTableItem.descriptionImages.length > 0 ? (
							<ChallengeDescriptionArea>
								{selectedTableItem.descriptionImages.map((descriptionImage) => (
									<ChallengeDescriptionImage
										key={descriptionImage}
										src={`${IMG_STORAGE_URL}/campaign_description_img/${descriptionImage}`}
									/>
								))}
							</ChallengeDescriptionArea>
						) : null}
					</>
				) : (
					<Empty description="선택된 챌린지가 없습니다." />
				)}
			</PreviewModalBodyContainer>
		</Modal>
	)
}

const PreviewModalBodyContainer = styled.div`
	width: calc(100% + 48px);
	height: calc(80vh - 52px);
	overflow: scroll;
	-ms-overflow-style: none;
	scrollbar-width: none;
	&::-webkit-scrollbar {
		display: none;
	}
	margin-left: -24px;
	margin-bottom: -20px;
	border-bottom-left-radius: 8px;
	border-bottom-right-radius: 8px;
	display: flex;
	flex-direction: column;
`

const ChallengeLinkArea = styled.div`
	padding: 0 4px;
	display: flex;
	flex-direction: row;
	align-items: center;
	border-top: 1px solid #d9d9d9;
`

const ChallengeLinkDescription = styled.div`
	color: #121212;
	font-size: 16px;
	font-style: normal;
	font-weight: 600;
	line-height: normal;
`

const ChallengeCoverArea = styled.div`
	width: 100%;
`

const ChallengeCoverImage = styled.img`
	width: 100%;
	aspect-ratio: 1 / 1;
	object-fit: cover;
`

const ChallengeNameArea = styled.div`
	margin-top: 15px;
	padding: 0 17px;
`

const ChallengeName = styled.div`
	font-size: 23px;
	font-weight: bold;
	line-height: 1.43;
	white-space: pre-wrap;
	word-break: break-all;
`

const CertificationArea = styled.div`
	margin: 25px 0;
	padding: 0 20px;
`

const CertificationKey = styled.div`
	color: #121212;
	font-size: 18px;
	line-height: 1.11;
	font-weight: bold;
	margin-bottom: 24px;
`

const CertificationContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: 15px;
`

const CertificationDuration = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
`

const CertificationDurationKey = styled.div`
	color: #121212;
	font-size: 16px;
	font-weight: bold;
	line-height: 19px;
	width: 70px;
	flex-grow: 0;
`

const CertificationDurationValue = styled.div`
	flex-grow: 1;
	color: #4e4e4e;
	font-size: 16px;
	font-weight: 500;
	line-height: 19px;
`

const CertificationTask = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
`

const CertificationTaskKey = styled.div`
	font-size: 16px;
	font-weight: 500;
	flex-grow: 0;
	width: 70px;
`

const CertificationTaskValue = styled.div`
	flex-grow: 1;
	font-size: 14px;
	font-weight: 500;
	padding: 12px 20px;
	background: #f7f7f7;
	border-radius: 10px;
	color: #000;
`

const CertificationMethodArea = styled.div`
	margin: 25px 0;
	padding: 0 20px;
`

const CertificationMethodTitle = styled.div`
	font-size: 18px;
	font-weight: bold;
	line-height: 1.11;
	margin-bottom: 18px;
`

const CertificationMethodImageWrapper = styled.div`
	display: flex;
	flex-direction: row;
	gap: 4%;
	overflow-x: scroll;
	-ms-overflow-style: none;
	scrollbar-width: none;
	&::-webkit-scrollbar {
		display: none;
	}
`

const CertificationMethodImage = styled.img`
	width: 48%;
`

const ChallengeRewardArea = styled.div`
	margin: 25px 0;
`

const ChallengeRewardTitle = styled.div`
	padding: 0 20px;
	font-size: 18px;
	font-weight: bold;
	line-height: 1.11;
	margin-bottom: 7px;
	color: #121212;
`

const ChallengeRewardSubTitle = styled.div`
	padding: 0 20px;
	font-size: 16px;
	font-weight: 500;
	line-height: normal;
	margin-bottom: 18px;
	color: #4e4e4e;
`

const HotdealItemWrapper = styled.div`
	display: flex;
	flex-direction: row;
	gap: 9px;
	padding: 0 22px;
`

const HotdealItemLeft = styled.div`
	flex-grow: 0;
`

const HotdealItemImageWrapper = styled.div`
	width: 90px;
	height: 90px;
	border-radius: 10px;
	background: #d9d9d9;
	overflow: hidden;
`

const HotdealItemImage = styled.img`
	width: 100%;
	height: 100%;
	object-fit: cover;
`

const HotdealItemRight = styled.div`
	flex-grow: 1;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
`

const HotdealItemName = styled.div`
	color: #121212;
	font-size: 16px;
	font-weight: 500;
	line-height: 22px;

	overflow: hidden;
	text-overflow: ellipsis;
	display: -webkit-box;
	-webkit-box-orient: vertical;
	-webkit-line-clamp: 2;
	white-space: pre-wrap;
	word-break: keep-all;
`

const HotdealItemPriceInfo = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
`

const HotdealItemBeforePrice = styled.div`
	color: #989898;
	font-size: 16px;
	font-weight: 500;
	line-height: normal;
	text-decoration-line: line-through;
`

const HotdealItemPriceBottom = styled.div`
	display: flex;
	flex-direction: row;
	gap: 3px;
`

const HotdealItemDiscount = styled.div`
	color: #de0000;
	font-size: 18px;
	font-weight: 700;
	line-height: normal;
`

const HotdealItemAfterPrice = styled.div`
	color: #121212;
	font-size: 18px;
	font-weight: 700;
	line-height: normal;
`

const ChallengeDescriptionArea = styled.div`
	display: flex;
	flex-direction: column;
	align-items: stretch;
	padding: 0 20px;
`

const ChallengeDescriptionImage = styled.img`
	width: 100%;
`
